import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '../../services/api';
import TelemetryAnalytics from './components/TelemetryAnalytics';
import LogoutButton from '../../components/LogoutButton';
interface ClientBrand {
    id: number;
    brand_name: string;
    brand_user_id: string | null;
    status: string;
    created_at: string | null;
}

export default function AgencyDashboard() {
    const navigate = useNavigate();
    const location = useLocation();
    const [clients, setClients] = useState<ClientBrand[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState<'brands' | 'analytics'>(
        (location.state as any)?.activeTab === 'analytics' || window.location.pathname === '/analytics'
            ? 'analytics'
            : 'brands'
    );

    const agencyId = localStorage.getItem('agency_id');

    useEffect(() => {
        if (!agencyId) {
            setError('No agency ID found. Please log in again.');
            setIsLoading(false);
            return;
        }
        fetchClients();
    }, [agencyId]);

    const fetchClients = async () => {
        try {
            setIsLoading(true);
            const response = await apiService.getAgencyClients(agencyId!);
            if (response.status === 'success' && response.data) {
                setClients(response.data.clients);
            } else {
                setError('Failed to load clients');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load agency data');
        } finally {
            setIsLoading(false);
        }
    };


    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600 font-medium">Loading dashboard...</p>
                </div>
            </div>
        );
    }

    if (error && !agencyId) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
                    <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-error-warning-line text-red-500 text-3xl"></i>
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 mb-2">Session Expired</h2>
                    <p className="text-gray-600 text-sm mb-6">{error}</p>
                    <button
                        onClick={() => navigate('/login')}
                        className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                    >
                        Go to Login
                    </button>
                </div>
            </div>
        );
    }

    // Only show brands that are fully onboarded and active
    const activeBrands = clients.filter(c => c.status === 'active');

    return (
        <div className="min-h-screen bg-white font-sans relative">
            {/* Top Right Controls */}
            <div className="absolute top-6 right-8 flex items-center gap-5">
                <button
                    onClick={fetchClients}
                    className="text-sm text-slate-400 hover:text-slate-600 flex items-center gap-1.5 transition-colors"
                    title="Refresh Dashboard"
                >
                    <i className="ri-refresh-line text-lg"></i>
                    Refresh
                </button>
                <LogoutButton
                    className="flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 transition-all"
                >
                    <i className="ri-logout-box-r-line text-[18px] text-slate-600"></i>

                    <span className="hidden lg:inline text-sm font-semibold text-slate-700">
                        Logout
                    </span>
                </LogoutButton>
            </div>

            {/* Header Section */}
            <div className="text-center pt-24 pb-10">
                <h1 className="text-[36px] font-bold text-slate-900 mb-3 tracking-tight">Welcome to Your Agency Dashboard</h1>
                <p className="text-slate-500 text-sm">Marketing4Sight helps you monitor and manage the marketing health of brands under you.</p>
            </div>

            {/* Tab Navigation Switcher */}
            <div className="max-w-7xl mx-auto px-6 mb-8 flex justify-center">
                <div className="flex bg-slate-100 p-1.5 rounded-2xl shadow-sm border border-slate-200/30">
                    <button
                        onClick={() => {
                            setActiveTab('brands');
                            window.history.replaceState(null, '', '/agency-dashboard');
                        }}
                        className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all duration-300 ${activeTab === 'brands'
                                ? 'bg-white text-slate-950 shadow-sm'
                                : 'text-slate-500 hover:text-slate-800'
                            }`}
                    >
                        <i className="ri-building-line text-sm"></i>
                        Client Brands
                    </button>
                    <button
                        onClick={() => {
                            setActiveTab('analytics');
                            window.history.replaceState(null, '', '/analytics');
                        }}
                        className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all duration-300 ${activeTab === 'analytics'
                                ? 'bg-white text-slate-950 shadow-sm'
                                : 'text-slate-500 hover:text-slate-800'
                            }`}
                    >
                        <i className="ri-line-chart-line text-sm"></i>
                        Telemetry Analytics
                    </button>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 pb-20">
                {activeTab === 'brands' ? (
                    activeBrands.length === 0 ? (
                        <div className="text-center py-20">
                            <div className="w-20 h-20 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                <i className="ri-building-line text-slate-300 text-3xl"></i>
                            </div>
                            <p className="text-slate-500 font-medium mb-2">No active brands yet</p>
                            <p className="text-slate-400 text-sm mb-6">Add brands and complete onboarding to see them here</p>
                            <button
                                onClick={() => navigate('/onboarding', { state: { returnToAddClients: true } })}
                                className="px-6 py-3 bg-teal-500 text-white rounded-xl text-sm font-semibold hover:bg-teal-600 transition-colors"
                            >
                                Add & Onboard Client Brands
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
                                {activeBrands.map((client) => {
                                    const seed = client.id || 1;
                                    const seoHealth = 70 + (seed % 20);
                                    const contentScore = 75 + ((seed * 2) % 20);
                                    const channelPerformance = 60 + ((seed * 3) % 30);
                                    const mediaRoi = 80 + ((seed * 5) % 15);

                                    return (
                                        <div
                                            key={client.id}
                                            className="bg-white rounded-2xl border border-slate-100 p-8 shadow-[0_2px_12px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300"
                                        >
                                            {/* Card Header */}
                                            <div className="flex justify-between items-start mb-2">
                                                <h3 className="text-[22px] font-bold text-slate-900 leading-tight">{client.brand_name}</h3>
                                                <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                                                    <span className="px-3 py-1 bg-orange-50 text-orange-500 text-[11px] font-bold rounded-full whitespace-nowrap tracking-wide">
                                                        Active on Marketing4Sight
                                                    </span>
                                                    <button className="w-8 h-8 flex items-center justify-center bg-slate-50 hover:bg-slate-100 border border-slate-100 rounded-lg transition-colors flex-shrink-0">
                                                        <i className="ri-bar-chart-2-fill text-slate-400"></i>
                                                    </button>
                                                </div>
                                            </div>

                                            <p className="text-slate-400 text-[11px] mb-8">Marketing Health Overview</p>

                                            {/* Health Metrics */}
                                            <div className="space-y-5 mb-10">
                                                <div className="flex items-center justify-between gap-4">
                                                    <span className="text-blue-500 w-[130px] text-xs font-semibold flex-shrink-0">SEO Health</span>
                                                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                                                        <div className="h-full bg-blue-500 rounded-full" style={{ width: `${seoHealth}%` }}></div>
                                                    </div>
                                                    <span className="text-slate-600 font-semibold text-[13px] w-9 text-right flex-shrink-0">{seoHealth}%</span>
                                                </div>
                                                <div className="flex items-center justify-between gap-4">
                                                    <span className="text-emerald-500 w-[130px] text-xs font-semibold flex-shrink-0">Content Score</span>
                                                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                                                        <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${contentScore}%` }}></div>
                                                    </div>
                                                    <span className="text-slate-600 font-semibold text-[13px] w-9 text-right flex-shrink-0">{contentScore}%</span>
                                                </div>
                                                {/* Channel Performance — Coming Soon */}
                                                <div className="flex items-center justify-between gap-4 relative">
                                                    <div className="absolute inset-0 bg-white/70 backdrop-blur-[2px] rounded-lg z-10 flex items-center justify-end pr-1">
                                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider bg-slate-100 px-2 py-0.5 rounded-full">Soon</span>
                                                    </div>
                                                    <span className="text-orange-400 w-[130px] text-xs font-semibold flex-shrink-0">Channel Performance</span>
                                                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                                                        <div className="h-full bg-orange-400 rounded-full" style={{ width: `${channelPerformance}%` }}></div>
                                                    </div>
                                                    <span className="text-slate-400 font-semibold text-[13px] w-9 text-right flex-shrink-0">{channelPerformance}%</span>
                                                </div>
                                                {/* Media ROI — Coming Soon */}
                                                <div className="flex items-center justify-between gap-4 relative">
                                                    <div className="absolute inset-0 bg-white/70 backdrop-blur-[2px] rounded-lg z-10 flex items-center justify-end pr-1">
                                                        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider bg-slate-100 px-2 py-0.5 rounded-full">Soon</span>
                                                    </div>
                                                    <span className="text-purple-400 w-[130px] text-xs font-semibold flex-shrink-0">Media ROI</span>
                                                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                                                        <div className="h-full bg-purple-400 rounded-full" style={{ width: `${mediaRoi}%` }}></div>
                                                    </div>
                                                    <span className="text-slate-400 font-semibold text-[13px] w-9 text-right flex-shrink-0">{mediaRoi}%</span>
                                                </div>
                                            </div>

                                        {/* CTA */}
                                        <button
                                            onClick={() => {
                                                localStorage.setItem('user_id', client.brand_user_id!);
                                                navigate('/marketing-dashboard', { state: { brand: client.brand_name } });
                                            }}
                                            className="w-full flex items-center justify-between text-teal-600 text-[13px] font-bold tracking-wide hover:text-teal-700 transition-colors group/btn"
                                        >
                                            View Marketing Dashboard
                                            <i className="ri-arrow-right-line text-lg transform group-hover/btn:translate-x-1 transition-transform"></i>
                                        </button>
                                    </div>
                                );
                            })}
                        </div>

                            {/* Add More Clients CTA */}
                            <div className="flex justify-center mt-2">
                                <button
                                    onClick={() => navigate('/onboarding', { state: { returnToAddClients: true } })}
                                    className="flex items-center gap-2.5 px-6 py-3 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition-colors text-[13px] font-bold shadow-sm"
                                >
                                    <i className="ri-add-line text-slate-400 font-normal"></i> Add More Clients
                                </button>
                            </div>
                        </>
                    )
                ) : (
                    import.meta.env.VITE_ENABLE_ANALYTICS === 'true' ? (
                        <TelemetryAnalytics />
                    ) : (
                        <div className="py-20 text-center bg-white rounded-2xl shadow-sm border border-slate-100">
                            <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-slate-100">
                                <i className="ri-time-line text-3xl text-slate-400"></i>
                            </div>
                            <h3 className="text-xl font-bold text-slate-800 mb-2">Analytics Coming Soon</h3>
                            <p className="text-slate-500 max-w-md mx-auto">We are actively building this feature. Analytics and telemetry insights will be available here shortly.</p>
                        </div>
                    )
                )}
            </div>
        </div>
    );
}
